import firebase from 'firebase/app'

import * as crud from './crud'
import { assert } from '../helpers'


/**
 * Get QR Codes from markets
 * @param {*} marketId - The id of the market
 * @param {*} options - CRUD options
 * @returns {Array<QRCodes>} List of QR Codes of the market
 */
export const getQrcodes = async (marketId, options = {}) => {
  const db = firebase.firestore()

  return crud.getAll(db.collection('shops').doc(marketId).collection('qrcodes'), {
    ...options,
    onlyItems: true,
  })
}


/**
 * Add a new QRCode
 * @param {string} marketId - The id of the market
 * @returns {Qrcode} The qrcode object
 */
export const addQrcode = async (marketId) => {
  const db = firebase.firestore()
  const ref = db.collection('shops').doc(marketId).collection('qrcodes').doc()

  return crud.set(ref, {
    value: `${marketId}-${ref.id}`,
  })
}


/**
 * Get a market by its id
 * @param {String} uid - The id of the market
 * @param {*} options - CRUD Options
 * @returns {Market} The market object
 */
export const getMarket = async (uid, options) => {
  const db = firebase.firestore()

  return crud.get(db.collection('shops').doc(uid), {
    ...options,
    map: async (doc, data) => {
      const qrcodes = await getQrcodes(doc.id)

      return {
        ...crud.getDefaultMapping(doc, data),
        qrcodes,
      }
    },
  })
}


/**
 * Get a market by its phone number
 * @param {String} phone - The phone number
 * @param {*} options - CRUD Options
 * @returns {Market} The market
 */
export const getMarketByPhone = async (phone, options = {}) => {
  const db = firebase.firestore()

  const [market, marketByContact] = await Promise.all([
    crud.get(db.collection('shops').where('phone', '==', phone), options),
    crud.get(db.collection('shops').where('contact_phone', '==', phone), options),
  ])

  return market || marketByContact
}


/**
 * Get a market by its email
 * @param {String} email - The email
 * @param {*} options - CRUD Options
 * @returns {Market} The market
 */
export const getMarketByEmail = async (email, options = {}) => {
  const db = firebase.firestore()
  const [market, marketByContact] = await Promise.all([
    crud.get(db.collection('shops').where('email', '==', email), options),
    crud.get(db.collection('shops').where('contact_email', '==', email), options),
  ])
  return market || marketByContact
}


/**
 * Get a market by the contact id
 * @param {*} contactId - The id of the contact
 * @param {*} options - CRUD Options
 * @returns {Market} The market
 */
export const getMarketByContactId = async (contactId, options = {}) => {
  const db = firebase.firestore()

  return crud.getAll(db.collection('shops').where('contact_id', '==', contactId), {
    ...options,
    first: true,
    map: async (doc, data) => {
      const qrcodes = await getQrcodes(doc.id)

      return {
        ...crud.getDefaultMapping(doc, data),
        qrcodes,
      }
    },
  })
}


/**
 * Get list of markets
 * @param {*} options - CRUD Options
 * @returns {Array<Market>} List of markets
 */
export const getMarkets = async (options = {}) => {
  const db = firebase.firestore()

  return crud.getAll(db.collection('shops'), options)
}


/**
 * Check if a market with email already exists
 * @param {*} email - The email
 * @param {*} options - Options of the request
 */
export const checkMarketEmail = async (email, options = {}) => {
  const userWithEmail = await getMarketByEmail(email, options)
  assert(!userWithEmail, `Un commerce avec l'email "${email}" existe déjà.`)
}


/**
 * Check if a market with phone already exists
 * @param {*} phone - The phone
 * @param {*} options - Options of the request
 */
export const checkMarketPhone = async (phone, options = {}) => {
  const userWithPhone = await getMarketByPhone(phone, options)

  assert(!userWithPhone, `Un commerce avec le numéro "${phone}" existe déjà.`)
}


/**
 * Add a new Market
 * @param {ShopData} data - The data of  the shop
 * @returns {Shop} The shop object
 */
export const addMarket = async (data) => {
  const {
    email,
    phone,
    contact_phone,
    contact_email,
  } = data
  const db = firebase.firestore()

  assert(email, 'Un email est obligatoire.')
  assert(contact_email, 'Un email de contact est obligatoire.')

  const [marketWithEmail, marketWithPhone, marketWithContactEmail, marketWithContactPhone] = await Promise.all([
    getMarketByEmail(email),
    getMarketByPhone(phone),
    getMarketByEmail(contact_email),
    getMarketByPhone(contact_phone),
  ])
  assert(!marketWithEmail, `Un commerce avec l'email "${data.email}" existe déjà.`)
  assert(!marketWithContactEmail, `Un commerce avec l'email "${data.contact_email}" existe déjà.`)
  assert(!phone || !marketWithPhone, `Un commerce avec le numéro de téléphone "${data.phone}" existe déjà.`)
  assert(!contact_phone || !marketWithContactPhone, `Un commerce avec le numéro de téléphone "${data.contact_phone}" existe déjà.`)

  return crud.add(db.collection('shops'), {
    ...data,
    visible: true,
    created_at: new Date(),
  })
}


/**
 * Edit attribute of a market object
 * @param {String} marketId - The id of the market
 * @param {Values} values - List of editable fields
 * @returns {Promise}
 */
export const editMarket = async (marketId, values = {}) => {
  const db = firebase.firestore()
  const promises = []
  const market = await getMarket(marketId)

  if (values.phone && values.phone !== market.phone) {
    promises.push(checkMarketPhone(values.phone))
  }

  if (values.email && values.email !== market.email) {
    promises.push(checkMarketEmail(values.email))
  }

  if (values.contact_phone && values.contact_phone !== market.contact_phone) {
    promises.push(checkMarketPhone(values.contact_phone))
  }

  if (values.contact_email && values.contact_email !== market.contact_email) {
    promises.push(checkMarketEmail(values.contact_email))
  }

  return crud.edit(db.collection('shops').doc(marketId), values)
}
